import { useState } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Building2, BedDouble, UtensilsCrossed, MapPin,
  Star, DollarSign, Shield, HelpCircle, Globe, ExternalLink,
  Loader2, Clock, Navigation, Search, ThumbsUp, ThumbsDown,
  Users, Briefcase, Award, Target, ChevronDown, Link2,
} from "lucide-react";
import SourceLinksBlock from "./SourceLinksBlock";

function ensureUrl(url) {
  if (!url || typeof url !== "string") return "";
  const t = url.trim();
  return t.startsWith("http") ? t : `https://${t}`;
}

function ProvenanceBadge({ source, timestamp, confidence }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {source && <span className="text-[8px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400/50 font-medium">{source}</span>}
      {confidence != null && confidence > 0 && <span className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/25">{Math.round(confidence * 100)}%</span>}
      {timestamp && <span className="text-[8px] text-white/15">{new Date(timestamp).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}</span>}
    </div>
  );
}

function AccordionSection({ icon: Icon, title, provenance, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="border-b border-white/[0.04] last:border-0" data-testid={testId}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-2.5 group cursor-pointer"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-white/25" />
          <span className="text-white/50 text-[10px] uppercase tracking-wider font-semibold group-hover:text-white/70 transition-colors">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {provenance && <ProvenanceBadge {...provenance} />}
          <ChevronDown className={`w-3.5 h-3.5 text-white/20 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

function DataRow({ label, value, mono, link, unit }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <div className="flex justify-between items-baseline py-0.5">
      <span className="text-white/30 text-xs">{label}</span>
      {link ? (
        <a href={ensureUrl(link)} target="_blank" rel="noopener noreferrer" className="text-blue-400/60 hover:text-blue-400/80 text-xs flex items-center gap-1 max-w-[60%] truncate">
          {value}<ExternalLink className="w-2.5 h-2.5 shrink-0" />
        </a>
      ) : (
        <span className={`text-white/60 text-xs ${mono ? "font-mono" : ""} text-right max-w-[60%]`}>
          {typeof value === "boolean" ? "Ja" : value}{unit ? ` ${unit}` : ""}
        </span>
      )}
    </div>
  );
}

function FacilityChip({ label, active }) {
  if (!active) return null;
  return <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400/70 font-medium">{label}</span>;
}

function normalizeAI(raw) {
  const defaults = {
    room_types: [], facilities: {}, policies: {}, location_intelligence: {},
    hotel_structure: {}, mice_intelligence: {}, business_intelligence: {},
    summary: {}, positioning: [], experience_tags: [],
  };
  if (!raw || typeof raw !== "object") return defaults;
  return {
    ...defaults, ...raw,
    room_types: raw.room_types || [],
    facilities: raw.facilities || {},
    policies: raw.policies || {},
    location_intelligence: raw.location_intelligence || raw.location || {},
  };
}

function normalizeOTA(raw) {
  const defaults = {
    ota_sources: [], review_intelligence: {}, price_intelligence: {},
    amenities_extracted: [], faq: [],
  };
  if (!raw || typeof raw !== "object") return defaults;
  const otaSources = raw.ota_sources || (raw.sources_found || []).map(s => ({
    source_name: s.source_name || s.source || "", property_url: s.property_url || s.source_url || s.estimated_url || "",
    discovery_query: s.discovery_query || "", is_property_page: s.is_property_page ?? true, confidence: s.confidence || 0, extraction_timestamp: s.extraction_timestamp || "",
  }));
  const reviewIntel = { ...(raw.review_intelligence || {}) };
  if (!reviewIntel.booking_rating && raw.rating_estimate) reviewIntel.booking_rating = raw.rating_estimate;
  if (!reviewIntel.review_count && raw.review_count_estimate) reviewIntel.review_count = raw.review_count_estimate;
  const priceIntel = { ...(raw.price_intelligence || {}) };
  if (priceIntel.price_value == null && priceIntel.average_price_signal != null) priceIntel.price_value = priceIntel.average_price_signal;
  if (!priceIntel.price_currency && priceIntel.currency) priceIntel.price_currency = priceIntel.currency;
  if (!priceIntel.price_currency) priceIntel.price_currency = "USD";
  if (priceIntel.price_range_low == null && raw.price_range && typeof raw.price_range === "string") {
    const m = raw.price_range.match(/(\d+)\s*[-–]\s*(\d+)\s*(\w+)?/);
    if (m) { priceIntel.price_range_low = parseInt(m[1]); priceIntel.price_range_high = parseInt(m[2]); if (m[3]) priceIntel.price_currency = m[3]; }
  }
  let faq = raw.faq || [];
  if (faq.length > 0 && typeof faq[0] === "string") faq = faq.map(q => ({ question: q, answer: "" }));
  return { ...defaults, ...raw, ota_sources: otaSources, review_intelligence: reviewIntel, price_intelligence: priceIntel, amenities_extracted: raw.amenities_extracted || raw.amenities || [], faq };
}

export default function HotelIntelligencePanel({ hotel, token, onRefresh }) {
  const [enriching, setEnriching] = useState(false);
  const rawAI = (hotel.source_profiles || {}).ai_enrichment;
  const rawOTA = (hotel.source_profiles || {}).ota_data;
  const ai = normalizeAI(rawAI);
  const ota = normalizeOTA(rawOTA);
  const hasAI = rawAI && Object.keys(rawAI).length > 0;
  const hasOTA = rawOTA && Object.keys(rawOTA).length > 0;
  const hasEnrichment = hasAI || hasOTA;

  const triggerEnrich = async () => {
    setEnriching(true);
    try {
      toast.info("AI Enrichment wird gestartet...");
      await axios.post(`${API}/hotels/database/${hotel.id}/enrich`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hotel erfolgreich angereichert!");
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Enrichment fehlgeschlagen"); }
    setEnriching(false);
  };

  const roomTypes = ai.room_types || [];
  const facilities = ai.facilities || {};
  const policies = ai.policies || {};
  const location = ai.location_intelligence || {};
  const structure = ai.hotel_structure || {};
  const reviewIntel = ota.review_intelligence || {};
  const priceIntel = ota.price_intelligence || {};
  const faq = ota.faq || [];
  const amenities = ota.amenities_extracted || [];
  const mice = ai.mice_intelligence || {};
  const biz = ai.business_intelligence || {};
  const mergedFacilities = (hotel.source_profiles || {}).facilities_merged || [];
  const hasReviewInsights = (reviewIntel.top_positive_points || []).length > 0 || (reviewIntel.top_negative_points || []).length > 0;
  const hasRatings = reviewIntel.booking_rating || reviewIntel.google_rating || reviewIntel.tripadvisor_rating;
  const hasPriceData = priceIntel.price_value || priceIntel.average_price_signal || priceIntel.price_range_low;

  return (
    <div className="space-y-3" data-testid="hotel-intelligence-panel">
      {/* Enrichment Status Bar */}
      <div className="flex items-center justify-between bg-white/[0.02] rounded-lg border border-white/[0.06] px-3 py-2" data-testid="enrichment-status-bar">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${hotel.enrichment_status === "enriched" ? "bg-green-400" : hotel.enrichment_status === "partial" ? "bg-amber-400" : hotel.enrichment_status === "failed" ? "bg-red-400" : "bg-amber-400"}`} />
            <span className="text-white/50 text-xs font-medium" data-testid="enrichment-status-text">
              {hotel.enrichment_status === "enriched" ? "Angereichert" : hotel.enrichment_status === "partial" ? "Teilweise angereichert" : hotel.enrichment_status === "failed" ? "Fehlgeschlagen" : "Ausstehend"}
            </span>
          </div>
          {hotel.last_enriched && (
            <span className="text-white/20 text-[10px] flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(hotel.last_enriched).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <button onClick={triggerEnrich} disabled={enriching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-purple-500/15 text-purple-400/80 hover:bg-purple-500/25 hover:text-purple-300 transition-colors disabled:opacity-30"
          data-testid="enrich-btn">
          {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {hasEnrichment ? "Erneut anreichern" : "Mit AI anreichern"}
        </button>
      </div>

      {!hasEnrichment && (
        <div className="text-center py-8 text-white/15 text-sm" data-testid="no-enrichment-msg">
          Noch keine AI-Anreicherung vorhanden. Klicke auf "Mit AI anreichern" um zu starten.
        </div>
      )}

      {hasEnrichment && (
        <div className="bg-white/[0.015] rounded-xl border border-white/[0.06] p-3" data-testid="enrichment-data">

          {/* OPEN BY DEFAULT: Source Links */}
          <AccordionSection icon={Link2} title="Quellen & Links" defaultOpen testId="section-sources">
            <SourceLinksBlock sourceProfiles={hotel.source_profiles} hotel={hotel} />
          </AccordionSection>

          {/* OPEN BY DEFAULT: Hotel Structure */}
          {(structure.total_rooms != null || structure.suites != null || structure.property_type || roomTypes.length > 0) && (
            <AccordionSection icon={Building2} title="Hotel-Struktur & Zimmertypen" defaultOpen
              provenance={{ source: ai.room_types_source || ai.model, timestamp: ai.enriched_at }} testId="section-structure">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                {structure.total_rooms != null && <div className="bg-white/[0.03] rounded-lg p-2.5 text-center" data-testid="stat-total-rooms"><p className="text-white/70 text-lg font-semibold font-mono">{structure.total_rooms}</p><p className="text-white/25 text-[10px]">Zimmer</p></div>}
                {structure.suites != null && <div className="bg-white/[0.03] rounded-lg p-2.5 text-center" data-testid="stat-suites"><p className="text-white/70 text-lg font-semibold font-mono">{structure.suites}</p><p className="text-white/25 text-[10px]">Suiten</p></div>}
                {structure.villas != null && <div className="bg-white/[0.03] rounded-lg p-2.5 text-center"><p className="text-white/70 text-lg font-semibold font-mono">{structure.villas}</p><p className="text-white/25 text-[10px]">Villen</p></div>}
                {structure.property_type && <div className="bg-white/[0.03] rounded-lg p-2.5 text-center"><p className="text-white/70 text-sm font-semibold capitalize">{structure.property_type}</p><p className="text-white/25 text-[10px]">Typ</p></div>}
              </div>
              {roomTypes.length > 0 && (
                <div data-testid="room-types-list">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-white/25 text-[10px] uppercase tracking-wider">Zimmertypen</p>
                    {ai.room_types_source && <span className="text-[8px] px-1 py-0.5 rounded bg-teal-500/10 text-teal-400/50">via {ai.room_types_source}</span>}
                  </div>
                  <div className="space-y-1.5">
                    {roomTypes.map((rt, i) => (
                      <div key={i} className="bg-white/[0.025] rounded-lg px-3 py-2" data-testid={`room-type-${i}`}>
                        <div className="flex items-center gap-2">
                          <BedDouble className="w-3 h-3 text-white/20 shrink-0" />
                          <span className="text-white/70 text-xs font-medium">{rt.name}</span>
                          {rt.max_guests && <span className="text-white/20 text-[10px]">{rt.max_guests} Gste</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1 ml-5">
                          {rt.bed_configuration && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/30">{rt.bed_configuration}</span>}
                          {rt.size_sqm && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/30">{rt.size_sqm} m2</span>}
                          {rt.view_type && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400/50">{rt.view_type}</span>}
                          {(rt.key_features || []).map((f, j) => <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/50">{f}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </AccordionSection>
          )}

          {/* OPEN BY DEFAULT: Business Intelligence */}
          {(biz.hotel_category || biz.sales_angle || biz.partnership_potential || (biz.target_guest_profile || []).length > 0) && (
            <AccordionSection icon={Target} title="Business Intelligence" defaultOpen
              provenance={{ source: ai.model, timestamp: ai.enriched_at }} testId="section-business">
              <div className="space-y-2.5" data-testid="business-intelligence">
                {biz.hotel_category && <div className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-amber-400/40" /><span className="text-white/60 text-xs font-medium">{biz.hotel_category}</span></div>}
                {(biz.target_guest_profile || []).length > 0 && (
                  <div data-testid="target-guests"><p className="text-white/30 text-[10px] font-medium mb-1">Zielgruppe</p>
                    <div className="flex flex-wrap gap-1.5">{biz.target_guest_profile.map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400/60 font-medium capitalize">{t}</span>)}</div>
                  </div>
                )}
                {biz.sales_angle && <div data-testid="sales-angle"><p className="text-white/30 text-[10px] font-medium mb-1">Sales Angle</p><span className="text-[11px] px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400/60 font-medium inline-block">{biz.sales_angle}</span></div>}
                {biz.partnership_potential && (
                  <div className="flex items-center gap-2" data-testid="partnership-potential">
                    <p className="text-white/30 text-[10px] font-medium">Partnership</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${biz.partnership_potential === "high" ? "bg-green-500/15 text-green-400/80" : biz.partnership_potential === "medium" ? "bg-amber-500/12 text-amber-400/60" : "bg-white/[0.04] text-white/30"}`}>
                      {biz.partnership_potential.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </AccordionSection>
          )}

          {/* OPEN BY DEFAULT: Price + Review Snapshot */}
          {(hasPriceData || hasRatings || hasReviewInsights) && (
            <AccordionSection icon={Star} title="Preis & Bewertungen" defaultOpen
              provenance={{ source: ota.model, timestamp: ota.extracted_at }} testId="section-price-review">
              {/* Price */}
              {hasPriceData && (
                <div className="mb-3">
                  <div className="grid grid-cols-3 gap-3 mb-2" data-testid="price-signals">
                    {priceIntel.price_range_low != null && <div className="bg-white/[0.03] rounded-lg p-2 text-center"><p className="text-white/60 text-sm font-semibold font-mono">{priceIntel.price_currency} {priceIntel.price_range_low}</p><p className="text-white/20 text-[9px]">Ab</p></div>}
                    {(priceIntel.price_value || priceIntel.average_price_signal) != null && <div className="bg-white/[0.03] rounded-lg p-2 text-center"><p className="text-white/60 text-sm font-semibold font-mono">{priceIntel.price_currency} {priceIntel.price_value || priceIntel.average_price_signal}</p><p className="text-white/20 text-[9px]">Durchschnitt</p></div>}
                    {priceIntel.price_range_high != null && <div className="bg-white/[0.03] rounded-lg p-2 text-center"><p className="text-white/60 text-sm font-semibold font-mono">{priceIntel.price_currency} {priceIntel.price_range_high}</p><p className="text-white/20 text-[9px]">Bis</p></div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {priceIntel.price_positioning && <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${priceIntel.price_positioning === "ultra_luxury" ? "bg-amber-500/15 text-amber-400/80" : priceIntel.price_positioning === "upper_luxury" ? "bg-purple-500/15 text-purple-400/70" : priceIntel.price_positioning === "upscale" ? "bg-blue-500/12 text-blue-400/60" : priceIntel.price_positioning === "mid_range" ? "bg-teal-500/12 text-teal-400/60" : "bg-white/[0.06] text-white/40"}`} data-testid="price-positioning">{priceIntel.price_positioning.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>}
                    {priceIntel.price_source && <span className="text-white/20 text-[10px]">Quelle: {priceIntel.price_source}</span>}
                  </div>
                </div>
              )}
              {/* Ratings */}
              {hasRatings && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2" data-testid="review-ratings">
                  {reviewIntel.booking_rating != null && <div className="bg-blue-500/[0.06] rounded-lg p-2 text-center"><p className="text-blue-400/80 text-base font-bold font-mono">{reviewIntel.booking_rating}</p><p className="text-white/20 text-[9px]">Booking /10</p></div>}
                  {reviewIntel.google_rating != null && <div className="bg-amber-500/[0.06] rounded-lg p-2 text-center"><p className="text-amber-400/80 text-base font-bold font-mono">{reviewIntel.google_rating}</p><p className="text-white/20 text-[9px]">Google /5</p></div>}
                  {reviewIntel.tripadvisor_rating != null && <div className="bg-green-500/[0.06] rounded-lg p-2 text-center"><p className="text-green-400/80 text-base font-bold font-mono">{reviewIntel.tripadvisor_rating}</p><p className="text-white/20 text-[9px]">TripAdvisor /5</p></div>}
                  {reviewIntel.agoda_rating != null && <div className="bg-red-500/[0.06] rounded-lg p-2 text-center"><p className="text-red-400/80 text-base font-bold font-mono">{reviewIntel.agoda_rating}</p><p className="text-white/20 text-[9px]">Agoda /10</p></div>}
                </div>
              )}
              {reviewIntel.review_count != null && <p className="text-white/25 text-[10px] mb-2">Bewertungen: <span className="text-white/40 font-mono">{reviewIntel.review_count}</span></p>}
              {/* Guest insights */}
              {hasReviewInsights && (
                <div className="space-y-2 pt-2 border-t border-white/[0.04]" data-testid="review-insights">
                  {(reviewIntel.top_positive_points || []).length > 0 && (
                    <div data-testid="positive-points"><div className="flex items-center gap-1.5 mb-1"><ThumbsUp className="w-3 h-3 text-green-400/50" /><span className="text-green-400/50 text-[10px] font-medium">Gaste lieben</span></div>
                      <div className="flex flex-wrap gap-1.5">{reviewIntel.top_positive_points.map((p, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400/60">{p}</span>)}</div>
                    </div>
                  )}
                  {(reviewIntel.top_negative_points || []).length > 0 && (
                    <div data-testid="negative-points"><div className="flex items-center gap-1.5 mb-1"><ThumbsDown className="w-3 h-3 text-red-400/40" /><span className="text-red-400/40 text-[10px] font-medium">Kritikpunkte</span></div>
                      <div className="flex flex-wrap gap-1.5">{reviewIntel.top_negative_points.map((p, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/8 text-red-400/50">{p}</span>)}</div>
                    </div>
                  )}
                  {reviewIntel.guest_profile && <div data-testid="guest-profile"><div className="flex items-center gap-1.5 mb-1"><Users className="w-3 h-3 text-white/25" /><span className="text-white/30 text-[10px] font-medium">Gasteprofil</span></div><p className="text-white/45 text-[11px] ml-[18px]">{reviewIntel.guest_profile}</p></div>}
                </div>
              )}
            </AccordionSection>
          )}

          {/* CLOSED BY DEFAULT: Facilities */}
          {(Object.values(facilities).some(v => v) || amenities.length > 0 || mergedFacilities.length > 0) && (
            <AccordionSection icon={UtensilsCrossed} title="Einrichtungen & Amenities"
              provenance={{ source: ai.model, timestamp: ai.enriched_at }} testId="section-facilities">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                <DataRow label="Restaurants" value={facilities.restaurants} mono />
                <DataRow label="Bars" value={facilities.bars} mono />
                <DataRow label="Meeting-Raume" value={facilities.meeting_rooms} mono />
              </div>
              {/* Merged facilities from all sources */}
              {mergedFacilities.length > 0 ? (
                <div className="mt-2" data-testid="merged-facilities">
                  <p className="text-white/20 text-[9px] uppercase tracking-wider mb-1">Alle Quellen (gemergt)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {mergedFacilities.map((f, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400/70 font-medium">{f}</span>)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="facility-chips">
                    <FacilityChip label="Spa" active={facilities.spa} /><FacilityChip label="Pool" active={facilities.swimming_pool} /><FacilityChip label="Fitness" active={facilities.fitness_center} /><FacilityChip label="Business Center" active={facilities.business_center} /><FacilityChip label="Parking" active={facilities.parking} /><FacilityChip label="Transfer" active={facilities.airport_transfer} /><FacilityChip label="WiFi" active={facilities.wifi} /><FacilityChip label="Family" active={facilities.family_friendly} />
                  </div>
                  {amenities.length > 0 && (
                    <div className="mt-3" data-testid="ota-amenities">
                      <div className="flex items-center gap-2 mb-1"><p className="text-white/20 text-[9px] uppercase tracking-wider">OTA Amenities</p>{ota.bedroom_villas_priority && <span className="text-[8px] px-1 py-0.5 rounded bg-teal-500/10 text-teal-400/50">BedroomVillas</span>}</div>
                      <div className="flex flex-wrap gap-1">{amenities.map((a, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400/50">{a}</span>)}</div>
                    </div>
                  )}
                </>
              )}
            </AccordionSection>
          )}

          {/* CLOSED BY DEFAULT: MICE Intelligence */}
          {(mice.mice_capability_score > 0 || mice.conference_rooms || mice.event_capacity) && (
            <AccordionSection icon={Briefcase} title="MICE Intelligence"
              provenance={{ source: ai.model, timestamp: ai.enriched_at }} testId="section-mice">
              <div className="flex items-center gap-3 mb-3" data-testid="mice-score">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold font-mono ${mice.mice_capability_score >= 3 ? "bg-green-500/15 text-green-400/80" : mice.mice_capability_score >= 2 ? "bg-blue-500/15 text-blue-400/70" : mice.mice_capability_score >= 1 ? "bg-amber-500/12 text-amber-400/60" : "bg-white/[0.04] text-white/25"}`}>{mice.mice_capability_score || 0}</div>
                <div><p className="text-white/60 text-xs font-medium">{mice.mice_capability_score >= 3 ? "Voll MICE-fahig" : mice.mice_capability_score >= 2 ? "Mittlere Event-Fahigkeit" : mice.mice_capability_score >= 1 ? "Kleine Meetings" : "Kein MICE-Signal"}</p><p className="text-white/25 text-[10px]">Score (0-3)</p></div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1"><DataRow label="Konferenzraume" value={mice.conference_rooms} mono /><DataRow label="Meeting Spaces" value={mice.meeting_spaces} mono /><DataRow label="Event-Kapazitat" value={mice.event_capacity} mono /><DataRow label="Hochzeit" value={mice.wedding_capacity} mono /></div>
            </AccordionSection>
          )}

          {/* CLOSED BY DEFAULT: Policies & FAQ */}
          {(policies.check_in_time || policies.check_out_time || policies.cancellation_policy || faq.length > 0) && (
            <AccordionSection icon={Shield} title="Policies & FAQ"
              provenance={{ source: ai.policies_source || ai.model, timestamp: ai.enriched_at }} testId="section-policies">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                <DataRow label="Check-in" value={policies.check_in_time} /><DataRow label="Check-out" value={policies.check_out_time} />
                {policies.cancellation_policy && <div className="col-span-2"><DataRow label="Stornierung" value={policies.cancellation_policy} /></div>}
                {policies.child_policy && <div className="col-span-2"><DataRow label="Kinder" value={policies.child_policy} /></div>}
                <DataRow label="Haustiere" value={policies.pet_policy} /><DataRow label="Zahlung" value={policies.payment_policy} />
              </div>
              {faq.length > 0 && (
                <div className="mt-2" data-testid="faq-list"><p className="text-white/20 text-[9px] uppercase tracking-wider mb-1">FAQ</p>
                  <div className="space-y-2">{faq.map((item, i) => { const q = typeof item === "string" ? item : item.question; const a = typeof item === "object" ? item.answer : "";
                    return (<div key={i} className="bg-white/[0.02] rounded-lg px-3 py-2"><div className="flex items-start gap-1.5 text-white/50 text-[11px] font-medium"><HelpCircle className="w-3 h-3 shrink-0 mt-0.5 text-white/25" /><span>{q}</span></div>{a ? <p className="text-white/35 text-[11px] ml-[18px] mt-1">{a}</p> : <p className="text-white/15 text-[10px] ml-[18px] mt-0.5 italic">Antwort nicht verfugbar</p>}</div>);
                  })}</div>
                </div>
              )}
            </AccordionSection>
          )}

          {/* CLOSED BY DEFAULT: Location Intelligence detail */}
          {(location.destination_type || location.nearest_airport || location.coordinates?.lat) && (
            <AccordionSection icon={MapPin} title="Location Details"
              provenance={{ source: ai.model, timestamp: ai.enriched_at }} testId="section-location">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {location.destination_type && <DataRow label="Destination" value={location.destination_type} />}
                {location.region && <DataRow label="Region" value={location.region} />}
                {location.nearest_airport && <DataRow label="Flughafen" value={location.nearest_airport} />}
                {location.airport_distance_km != null && <DataRow label="Entfernung" value={location.airport_distance_km} unit="km" mono />}
                {location.coordinates?.lat != null && <DataRow label="Koordinaten" value={`${location.coordinates.lat}, ${location.coordinates.lng}`} mono />}
              </div>
            </AccordionSection>
          )}

          {/* CLOSED BY DEFAULT: Soulful Story */}
          {ai.summary?.soulful_story && (
            <AccordionSection icon={Sparkles} title="Soulful Story"
              provenance={{ source: ai.model, timestamp: ai.enriched_at }} testId="section-story">
              <p className="text-white/50 text-xs italic leading-relaxed" data-testid="soulful-story-text">"{ai.summary.soulful_story}"</p>
              {ai.summary?.short_description && <p className="text-white/30 text-[11px] mt-2">{ai.summary.short_description}</p>}
              {(ai.positioning || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">{ai.positioning.map(p => <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400/50 capitalize">{p}</span>)}</div>}
              {(ai.experience_tags || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{ai.experience_tags.map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/50">{t}</span>)}</div>}
            </AccordionSection>
          )}
        </div>
      )}
    </div>
  );
}
